import { useState } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { LargeOrderFeeReason } from '@/types';

interface LargeOrderFeeInfoProps {
  /** Server-rendered title + message. The component shows it verbatim. */
  title: string;
  message: string;
}

/**
 * The ⓘ beside a fee (BOXY-311 §9.1.3, BOXY-113 §9.3): opens on hover AND on
 * click/tap, so it works with a mouse, a touchpad and a touch screen alike.
 * The text is the backend's — never composed here.
 */
export const FeeInfo = ({ title, message }: LargeOrderFeeInfoProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type='button'
        aria-label={String(t('fee_info_more', 'More information'))}
        onClick={(e) => {
          // OPEN, never toggle: with a mouse, hover has already opened it, and
          // a toggling click would shut it on the very click meant to read it.
          // preventDefault skips Radix's own toggle. Closing is mouse-leave,
          // an outside click or Esc.
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className='inline-flex items-center text-sd-muted-foreground hover:text-sd-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring rounded-full'
      >
        <Info className='h-3.5 w-3.5' strokeWidth={2} />
      </PopoverTrigger>
      <PopoverContent
        collisionPadding={16}
        className='w-72 text-sm'
        onClick={(e) => e.stopPropagation()}
      >
        {title ? <p className='font-semibold mb-1'>{title}</p> : null}
        <p className='text-sd-muted-foreground leading-relaxed'>{message}</p>
      </PopoverContent>
    </Popover>
  );
};

interface LargeOrderFeeBreakdownProps {
  deliveryFee: number;
  fee: number;
  reason?: LargeOrderFeeReason | null;
}

/**
 * "Delivery 4,000 + Large order fee 3,000 ⓘ" — shown ONLY when a fee applies
 * (§9.1.2); the total stays the prominent number elsewhere on the row.
 */
export const LargeOrderFeeBreakdown = ({
  deliveryFee,
  fee,
  reason,
}: LargeOrderFeeBreakdownProps) => {
  const { t } = useTranslation();

  if (!fee || !reason) {
    return null;
  }

  return (
    <span
      className='inline-flex items-center gap-1 text-xs text-sd-muted-foreground'
      data-testid='large-order-fee-breakdown'
    >
      <span className='tabular-nums'>
        {String(t('delivery_fee_short', 'Delivery'))} {Math.round(deliveryFee).toLocaleString()} +{' '}
        {String(t('large_order_fee', 'Large order fee'))} {Math.round(fee).toLocaleString()}
      </span>
      <FeeInfo title={reason.title} message={reason.message} />
    </span>
  );
};
